import {
  acceptRGPD,
  checkAuth,
  deleteAuthUser,
  deleteAuthUserAvatar,
  emailLogin,
  emailRegister,
  finishOnboarding as finishOnboardingService,
  logout,
  SocialLogin,
  SocialLoginCallback,
  UpdateAuthUser,
  updateAuthUserAvatar,
  updateAuthUserLang,
  updateAuthUserPassword,
} from '@/services/authService';
import {
  AuthState,
  EmailLoginCredentials,
  EmailRegisterCredentials,
} from '@/types/auth';
import { Lang, UpdateAuthUserPasswordForm, User } from '@/types/user';
import toast from 'react-hot-toast';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

export const useAuthStore = create<AuthState>()(
  immer<AuthState>((set, get) => ({
    isAuth: false,
    loading: false,
    user: null,
    avatarExists: false,

    emailLogin: async (credentials: EmailLoginCredentials) => {
      try {
        set(state => {
          state.loading = true;
        });
        await emailLogin(credentials);
        await get().checkAuth();
        set(state => {
          state.isAuth = true;
        });
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    socialLogin: async (provider: string) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { data } = await SocialLogin(provider);

        window.location.href = data;
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    socialLoginCallback: async (provider: string, code?: string) => {
      try {
        const { data } = await SocialLoginCallback(provider, code);

        set(state => {
          state.isAuth = true;
          state.user = data;
        });
      } catch (error: unknown) {
        set({ isAuth: false, user: null });
        throw error;
      }
    },

    emailRegister: async (credentials: EmailRegisterCredentials) => {
      set(state => {
        state.loading = true;
      });
      try {
        const res = await emailRegister(credentials);
        return res;
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    logout: async () => {
      try {
        set(state => {
          state.loading = true;
        });
        localStorage.removeItem('last_path');

        await logout();
        set(state => {
          state.isAuth = false;
          state.user = null;
        });
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    checkAuth: async () => {
      try {
        set(state => {
          state.loading = true;
        });
        const res = await checkAuth();
        set(state => {
          state.user = res.data.user;
          state.isAuth = res.data.isAuthenticated;
        });
        return res;
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    updateAuthUser: async (newUser: User) => {
      const { message, data } = await UpdateAuthUser(newUser);
      toast.success(message);
      set(state => {
        state.user = data;
      });
      return { message, data };
    },

    updateAuthUserAvatar: async (newAvatar: string) => {
      const { data, message } = await updateAuthUserAvatar(newAvatar);
      set(state => {
        if (state.user) {
          state.user = data;
        }
      });
      return { data, message };
    },

    deleteAuthUserAvatar: async () => {
      const { data, message } = await deleteAuthUserAvatar();
      set(state => {
        if (state.user) {
          state.user = data;
        }
      });
      return { data, message };
    },

    updateAuthUserPassword: async (data: UpdateAuthUserPasswordForm) => {
      try {
        set(state => {
          state.loading = true;
        });
        const res = await updateAuthUserPassword(data);
        return res;
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    updateAuthUserLang: async (lang: Lang): Promise<void> => {
      try {
        set(state => {
          state.loading = true;
        });
        const { data } = await updateAuthUserLang(lang);
        set(state => {
          if (state.user) {
            state.user.lang = data;
          }
        });
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    finishOnboarding: async () => {
      try {
        set(state => {
          state.loading = true;
        });
        await finishOnboardingService();

        set(state => {
          if (state.user) {
            state.user.finished_onboarding = true;
          }
        });
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    deleteAuthUser: async () => {
      try {
        set(state => {
          state.loading = true;
        });
        localStorage.removeItem('last_path');

        const res = await deleteAuthUser();
        set(state => {
          state.isAuth = false;
          state.user = null;
        });
        return res;
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    acceptRGPD: async () => {
      try {
        set(state => {
          state.loading = true;
        });
        const { data } = await acceptRGPD();
        set(state => {
          state.user = data;
        });
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },
  })),
);
